import { useState } from 'react'
import type { ReactNode } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Check, ChevronDown, Circle, Layers } from 'lucide-react'

import {
  describeLayer,
  getAvailableLayers,
  getMapApiKeys,
  partitionByCoverage,
  resolveMapLayers,
} from '@/lib/map-layers'
import type { MapLayerEntry, MapLayerGroup } from '@/lib/map-layers'
import { useActivityStore } from '@/store/activity-store'

const GROUP_LABELS: Record<MapLayerGroup, string> = {
  map: 'Map',
  satellite: 'Satellite',
  overlay: 'Overlays',
}

const ITEM_CLASSNAME =
  'hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground relative flex cursor-default items-start gap-2 rounded-sm py-1.5 pr-2 pl-7 text-sm outline-none'

const LABEL_CLASSNAME =
  'text-muted-foreground hover:bg-accent focus:bg-accent flex cursor-default items-center justify-between rounded-sm px-2 pt-1.5 pb-1 text-xs font-semibold outline-none'

/** Subheading inside a group (e.g. Satellite → Global / Regional). */
const SUBLABEL_CLASSNAME =
  'text-muted-foreground hover:bg-accent focus:bg-accent flex cursor-default items-center justify-between rounded-sm px-2 pt-1.5 pb-0.5 text-[11px] font-medium tracking-wide uppercase outline-none'

/** Which collapsible sections (group / subgroup keys) are folded shut. */
interface Folding {
  collapsed: ReadonlySet<string>
  toggle: (key: string) => void
}

/**
 * A section heading that folds its items away. A menu item rather than a
 * plain button so it keeps the menu's roving focus and arrow-key
 * navigation; `onSelect` is prevented so toggling leaves the menu open.
 */
function SectionHeader({
  id,
  label,
  className,
  folding,
}: {
  id: string
  label: string
  className: string
  folding: Folding
}) {
  const open = !folding.collapsed.has(id)
  return (
    <DropdownMenu.Item
      className={className}
      aria-expanded={open}
      onSelect={(e) => {
        e.preventDefault()
        folding.toggle(id)
      }}
    >
      {label}
      <ChevronDown
        className={`h-3.5 w-3.5 transition-transform ${open ? '' : '-rotate-90'}`}
        aria-hidden="true"
      />
    </DropdownMenu.Item>
  )
}

const SUBGROUP_LABELS = {
  global: 'Global',
  regional: 'Regional (aerial photos)',
} as const

function BasemapItem({ entry }: { entry: MapLayerEntry }) {
  return (
    <DropdownMenu.RadioItem
      value={entry.id}
      className={ITEM_CLASSNAME}
      onSelect={(e) => e.preventDefault()}
    >
      <Indicator>
        <Circle className="h-2 w-2 fill-current" aria-hidden="true" />
      </Indicator>
      <ItemContent label={entry.label} note={describeLayer(entry)} />
    </DropdownMenu.RadioItem>
  )
}

/**
 * One basemap group's items. A group mixing worldwide and regional
 * (`coverage`) sources gets "Global" / "Regional (aerial photos)"
 * subheadings, each a nested labelled group, so the long Satellite list
 * stays scannable; a group with only one kind is listed flat.
 */
function BasemapGroupItems({
  groupId,
  entries,
  folding,
}: {
  groupId: string
  entries: MapLayerEntry[]
  folding: Folding
}) {
  const { global, regional } = partitionByCoverage(entries)
  if (global.length === 0 || regional.length === 0) {
    return entries.map((entry) => <BasemapItem key={entry.id} entry={entry} />)
  }
  return (['global', 'regional'] as const).map((kind) => {
    const id = `${groupId}:${kind}`
    return (
      <div key={kind} role="group" aria-label={SUBGROUP_LABELS[kind]}>
        <SectionHeader
          id={id}
          label={SUBGROUP_LABELS[kind]}
          className={SUBLABEL_CLASSNAME}
          folding={folding}
        />
        {!folding.collapsed.has(id) &&
          (kind === 'global' ? global : regional).map((entry) => (
            <BasemapItem key={entry.id} entry={entry} />
          ))}
      </div>
    )
  })
}

function ItemContent({ label, note }: { label: string; note?: string | undefined }) {
  return (
    <span className="flex flex-col">
      {label}
      {note != null && (
        <span className="text-muted-foreground text-xs">
          {/* Separates label and note in the item's accessible name. */}
          <span className="sr-only">, </span>
          {note}
        </span>
      )}
    </span>
  )
}

function Indicator({ children }: { children: ReactNode }) {
  return (
    <span className="absolute top-2 left-2 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenu.ItemIndicator>{children}</DropdownMenu.ItemIndicator>
    </span>
  )
}

/**
 * Compact on-map layer menu: one basemap (radio, grouped Map/Satellite,
 * Satellite split into Global / Regional; every group and subgroup
 * heading folds its items away),
 * any number of overlays (checkboxes), and the 3D terrain toggle. Built
 * entirely from the `MAP_LAYERS` registry -- entries needing an API key
 * that isn't configured are not rendered at all. Choices go straight to
 * the persisted UI-preferences slice; `ActivityMap` reacts to the store.
 *
 * Radix DropdownMenu supplies the menu semantics (`menuitemradio` /
 * `menuitemcheckbox` with `aria-checked`), roving focus, arrow-key and
 * typeahead navigation, Escape to close and focus return to the trigger.
 */
export function MapLayerControl() {
  const prefs = useActivityStore((s) => s.ui.mapLayers)
  const setMapBasemap = useActivityStore((s) => s.setMapBasemap)
  const setMapOverlay = useActivityStore((s) => s.setMapOverlay)
  const setMapTerrain3d = useActivityStore((s) => s.setMapTerrain3d)

  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set())
  const folding: Folding = {
    collapsed,
    toggle: (key) =>
      setCollapsed((prev) => {
        const next = new Set(prev)
        if (!next.delete(key)) next.add(key)
        return next
      }),
  }

  const keys = getMapApiKeys()
  const available = getAvailableLayers(keys)
  const resolved = resolveMapLayers(prefs, keys)
  const activeOverlayIds = new Set(resolved.overlays.map((o) => o.id))

  const byGroup = (group: MapLayerGroup): MapLayerEntry[] =>
    available.filter((e) => e.group === group)
  const basemapGroups = (['map', 'satellite'] as const).filter((g) => byGroup(g).length > 0)
  const overlays = byGroup('overlay')

  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label="Map layers"
          title="Map layers"
          className="border-border bg-background text-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring flex h-[29px] w-[29px] items-center justify-center rounded-md border shadow-sm outline-none focus-visible:ring-2"
        >
          <Layers className="h-4 w-4" aria-hidden="true" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={4}
          collisionPadding={8}
          className="border-border bg-popover text-popover-foreground z-50 max-h-[var(--radix-dropdown-menu-content-available-height)] w-64 overflow-y-auto rounded-md border p-1 shadow-md"
        >
          <DropdownMenu.RadioGroup
            aria-label="Basemap"
            value={resolved.basemap.id}
            onValueChange={setMapBasemap}
          >
            {basemapGroups.map((group, index) => (
              <div key={group} role="group" aria-label={GROUP_LABELS[group]}>
                {index > 0 && <DropdownMenu.Separator className="bg-border my-1 h-px" />}
                <SectionHeader
                  id={group}
                  label={GROUP_LABELS[group]}
                  className={LABEL_CLASSNAME}
                  folding={folding}
                />
                {!collapsed.has(group) && (
                  <BasemapGroupItems groupId={group} entries={byGroup(group)} folding={folding} />
                )}
              </div>
            ))}
          </DropdownMenu.RadioGroup>

          {overlays.length > 0 && (
            <div role="group" aria-label={GROUP_LABELS.overlay}>
              <DropdownMenu.Separator className="bg-border my-1 h-px" />
              <SectionHeader
                id="overlay"
                label={GROUP_LABELS.overlay}
                className={LABEL_CLASSNAME}
                folding={folding}
              />
              {(collapsed.has('overlay') ? [] : overlays).map((entry) => (
                <DropdownMenu.CheckboxItem
                  key={entry.id}
                  checked={activeOverlayIds.has(entry.id)}
                  onCheckedChange={(checked) => setMapOverlay(entry.id, checked)}
                  onSelect={(e) => e.preventDefault()}
                  className={ITEM_CLASSNAME}
                >
                  <Indicator>
                    <Check className="h-3.5 w-3.5" aria-hidden="true" />
                  </Indicator>
                  <ItemContent label={entry.label} note={describeLayer(entry)} />
                </DropdownMenu.CheckboxItem>
              ))}
            </div>
          )}

          <DropdownMenu.Separator className="bg-border my-1 h-px" />
          <DropdownMenu.CheckboxItem
            checked={resolved.terrain3d}
            onCheckedChange={setMapTerrain3d}
            onSelect={(e) => e.preventDefault()}
            className={ITEM_CLASSNAME}
          >
            <Indicator>
              <Check className="h-3.5 w-3.5" aria-hidden="true" />
            </Indicator>
            <ItemContent
              label="3D terrain"
              note="Mapterhorn elevation; right-drag or Ctrl+drag to tilt/rotate"
            />
          </DropdownMenu.CheckboxItem>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
